import { Graphics } from 'pixi.js';
import type { IPlatformVisual, PlatformVisualOptions } from './IRenderer';
import { PixiOverlayHost } from './PixiOverlayHost';

/**
 * PixiPlatformVisual — IPlatformVisual impl backed by a PIXI.Graphics on
 * the overlay's worldContainer. Same lazy-build pattern as PixiGraphics:
 * mutations before ensureReady() resolves are buffered and applied once
 * the underlying Graphics exists.
 *
 * Origin handling: Phaser's Rectangle default origin is (0.5, 0.5), so
 * `x`/`y` is the geometric center. We replicate that by drawing the rect
 * around the origin with an offset based on (originX, originY) and
 * positioning the Graphics at (x, y). Surface overlays (originY=0)
 * anchor at the top edge.
 *
 * Stroke handling: Phaser.Rectangle.setStrokeStyle stacks stroke on top
 * of fill. We track fill + stroke state here and redraw on every change.
 */
export class PixiPlatformVisual implements IPlatformVisual {
  public readonly width: number;
  public readonly height: number;

  private graphics: Graphics | null = null;
  private destroyed = false;

  // Pending state until lazy ensureReady() resolves
  private pendingX: number;
  private pendingY: number;
  private pendingVisible = true;
  private pendingAlpha: number;

  // Persistent draw state (used to redraw on stroke / fill changes)
  private color: number;
  private fillAlpha: number;
  private readonly originX: number;
  private readonly originY: number;
  private readonly depth: number;
  private strokeColor = 0;
  private strokeWidth = 0;

  constructor(opts: PlatformVisualOptions) {
    const {
      x, y, width, height, color,
      alpha = 1,
      depth = 0,
      originX = 0.5,
      originY = 0.5,
    } = opts;
    this.width = width;
    this.height = height;
    this.color = color;
    this.fillAlpha = 1; // fill alpha is multiplied by handle alpha; default 1
    this.depth = depth;
    this.originX = originX;
    this.originY = originY;
    this.pendingX = x;
    this.pendingY = y;
    this.pendingAlpha = alpha;

    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const g = new Graphics();
      g.zIndex = this.depth;
      g.x = this.pendingX;
      g.y = this.pendingY;
      g.visible = this.pendingVisible;
      g.alpha = this.pendingAlpha;
      this.graphics = g;
      this.redraw();
      PixiOverlayHost.addWorldChild(g);
    });
  }

  private redraw(): void {
    if (!this.graphics) return;
    const g = this.graphics;
    // Phaser's origin convention: x/y of the rect is at fraction
    // (originX, originY) of its bounds. Draw the rect around graphics
    // origin with a corresponding offset so positioning matches.
    const rectX = -this.width * this.originX;
    const rectY = -this.height * this.originY;
    g.clear();
    g.rect(rectX, rectY, this.width, this.height).fill({ color: this.color, alpha: this.fillAlpha });
    if (this.strokeWidth > 0) {
      g.rect(rectX, rectY, this.width, this.height).stroke({
        color: this.strokeColor,
        width: this.strokeWidth,
      });
    }
  }

  setVisible(visible: boolean): void {
    this.pendingVisible = visible;
    if (this.graphics) this.graphics.visible = visible;
  }

  setAlpha(alpha: number): void {
    this.pendingAlpha = alpha;
    if (this.graphics) this.graphics.alpha = alpha;
  }

  setPosition(x: number, y: number): void {
    this.pendingX = x;
    this.pendingY = y;
    if (this.graphics) {
      this.graphics.x = x;
      this.graphics.y = y;
    }
  }

  setStroke(color: number, lineWidth: number): void {
    this.strokeColor = color;
    this.strokeWidth = Math.max(0, lineWidth);
    if (this.graphics) this.redraw();
  }

  setFillColor(color: number, alpha?: number): void {
    this.color = color;
    if (alpha !== undefined) this.fillAlpha = alpha;
    if (this.graphics) this.redraw();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.graphics) {
      this.graphics.destroy();
      this.graphics = null;
    }
  }
}
